import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, Input, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ShopBanner, ShopSection, ShopSettings } from '@infrab4a/connect';
import { MessageService } from 'primeng/api';
import { ImageUploadComponent } from 'src/app/components/image-upload/image-upload.component';
import { ShopSettingsService } from 'src/app/connect-api/api/shop-settings/shop-settings.service';

import { LoaderService } from 'src/app/services/loader.service';
import {
  MENSMARKET_HOME_DOCUMENT_ID,
  MENSMARKET_HOME_HIGHLIGHT_IMAGE_ACCEPT,
  MENSMARKET_HOME_HIGHLIGHTS_PATH,
  MENSMARKET_HOME_ROUTE
} from '../mens-new-home.constants';

export type DisplayRules = {
  subscriberBuyer: boolean;
  notSubscriber: boolean;
  subscriberNotBuyer: boolean;
  topBadgeSubscriber: boolean;
  beautyProfile: boolean;
};

export type Highlight = {
  id: string;
  title: string;
  subtitle: string;
  link: string;
  image: string;
  altText: string;
  highlighted: boolean;
  starred: boolean;
  displayRules?: Array<Partial<DisplayRules>>;
};

export type Highlights = {
  type: 'HIGHLIGHTS';
  fixed: boolean;
  editable: boolean;
  highlights: Highlight[];
};

@Component({
  selector: 'app-mens-new-home-highlights-form',
  templateUrl: './highligths-form.component.html',
  styleUrl: './highligths-form.component.scss'
})
export class MensNewHomeHighlightsFormComponent
  extends ImageUploadComponent<any>
  implements OnInit
{
  override get filePath(): string {
    return MENSMARKET_HOME_HIGHLIGHTS_PATH;
  }

  override get model(): ShopBanner {
    return null;
  }

  override get modelId(): string | number {
    return crypto.randomUUID();
  }

  @Input() id: string | null = null;
  override form: FormGroup = new FormGroup({
    id: new FormControl<string>(''),
    type: new FormControl<string>('HIGHLIGHTS'),
    fixed: new FormControl<boolean>(false),
    editable: new FormControl<boolean>(true),
    highlights: new FormArray([])
  });
  fb = inject(FormBuilder);
  settings: Partial<ShopSettings> & { sections?: ShopSection[] };
  private messageService = inject(MessageService);
  private shopSettingsService = inject(ShopSettingsService);
  private activatedRoute = inject(ActivatedRoute);

  displayRuleOptions = [
    { label: 'Não se aplica', value: null },
    { label: 'Sim', value: true },
    { label: 'Não', value: false }
  ];

  async ngOnInit(): Promise<void> {
    this.settings = await this.shopSettingsService.getById(MENSMARKET_HOME_DOCUMENT_ID);
    this.id = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.id) {
      const section = this.settings.sections?.find((s) => s.id === this.id);

      if (section) {
        this.form.patchValue({ ...section, highlights: undefined });
        if (section.highlights) {
          section.highlights.forEach((highlight) => {
            const highlightGroup = this.fb.group({
              id: [highlight.id],
              title: [highlight.title, Validators.required],
              subtitle: [highlight.subtitle, Validators.required],
              link: [highlight.link, Validators.required],
              image: [highlight.image, Validators.required],
              altText: [highlight.altText, Validators.required],
              highlighted: [highlight.highlighted],
              starred: [highlight.starred],
              displayRules: this.fb.group({
                subscriberBuyer: [null],
                notSubscriber: [null],
                subscriberNotBuyer: [null],
                topBadgeSubscriber: [null],
                beautyProfile: [null]
              })
            });
            if (
              highlight.displayRules &&
              Array.isArray(highlight.displayRules)
            ) {
              for (const rule of highlight.displayRules) {
                for (const key in rule) {
                  if (highlightGroup.get('displayRules')?.get(key)) {
                    highlightGroup
                      .get('displayRules')
                      ?.get(key)
                      ?.setValue(rule[key]);
                  }
                }
              }
            }
            this.highlights.push(highlightGroup);
          });
        }
      }
    }

    console.log('this.settings', this.settings);
  }

  get highlights(): FormArray {
    return this.form.get('highlights') as FormArray;
  }

  addHighlight(): void {
    const highlight = new FormGroup({
      id: new FormControl<string>(crypto.randomUUID()),
      title: new FormControl('', Validators.required),
      subtitle: new FormControl('', Validators.required),
      link: new FormControl('', Validators.required),
      image: new FormControl('', Validators.required),
      altText: new FormControl('', Validators.required),
      highlighted: new FormControl(false),
      starred: new FormControl(false),
      displayRules: new FormGroup({
        subscriberBuyer: new FormControl(null),
        notSubscriber: new FormControl(null),
        subscriberNotBuyer: new FormControl(null),
        topBadgeSubscriber: new FormControl(null),
        beautyProfile: new FormControl(null)
      })
    });
    this.highlights.push(highlight);
  }

  removeHighlight(index: number): void {
    this.highlights.removeAt(index);
  }

  onDrop(event: CdkDragDrop<any[]>): void {
    moveItemInArray(
      this.highlights.controls,
      event.previousIndex,
      event.currentIndex
    );
    this.highlights.updateValueAndValidity();
  }

  async onSubmit(): Promise<void> {
    const starredCount = this.highlights.controls.filter(
      (control) => control.get('starred')?.value
    ).length;

    if (starredCount > 3) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não é permitido mais de 3 highlights destacados'
      });
      return;
    }

    if (this.form.valid) {
      LoaderService.showLoader();
      const newSection = this.getHighlightInfo();
      const sections = this.settings.sections || [];
      const existingIndex = sections.findIndex((s) => s.id === this.id);

      if (existingIndex >= 0) {
        sections[existingIndex] = newSection;
      } else {
        sections.push(newSection);
      }

      await this.shopSettingsService.update({
        id: this.settings.id,
        sections: sections
      });
    }

    await this.afterSubmit();
    LoaderService.showLoader(false);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Highlight salvo com sucesso'
    });

    this.router.navigate([MENSMARKET_HOME_ROUTE]);
  }

  private getHighlightInfo() {
    const formValue = this.form.value;
    const highlights = formValue.highlights.map((highlight: any) => {
      const displayRules = [];
      const rules = highlight.displayRules;
      for (const key in rules) {
        if (rules[key] !== null) {
          displayRules.push({ [key]: rules[key] });
        }
      }
      return {
        id: highlight.id,
        title: highlight.title,
        subtitle: highlight.subtitle,
        link: highlight.link,
        image: highlight.image,
        altText: highlight.altText,
        highlighted: highlight.highlighted,
        starred: highlight.starred,
        displayRules: displayRules.length > 0 ? displayRules : []
      };
    });

    return {
      id: formValue.id || crypto.randomUUID(),
      type: formValue.type,
      fixed: formValue.fixed,
      editable: formValue.editable,
      highlights
    };
  }

  get highlightImageAccept(): string {
    return MENSMARKET_HOME_HIGHLIGHT_IMAGE_ACCEPT;
  }

  get highlightWidth(): number {
    return 128;
  }

  get highlightHeight(): number {
    return 128;
  }
}
